/**
 * Native DXF writer entry point (docs/opencadstudio-parity/01-native-dxf-
 * export.md §3.1) — the replacement for the old reducing R12 DxfWriter.
 * Emits full R13+-style output (handles + owner + subclass markers) for
 * the selected version target (R14 … 2018):
 *
 *   HEADER → CLASSES → TABLES → BLOCKS → ENTITIES → OBJECTS → EOF
 *
 * `writeDxfNative(entities?, { version, blocks?, layers? })` — the WBLOCK
 * runner passes a dependency-closed subset via blocks/layers; the plain
 * file export passes neither and writes the whole database.
 */

import { useAppStore, type Entity, type Layer } from '../../../state/store';
import { useBlockStore, type BlockDef } from '../../../state/commandAuxStores';
import { useTextStyleStore } from '../../../tools/cad/TextTools';
import { useDimStyleStore } from '../../../tools/cad/DimensioningTools';
import { HandleAllocator } from './handleAllocator';
import { DxfStreamWriter } from './DxfStreamWriter';
import {
    type DxfVersion, type DxfVersionInfo, type DxfWriteContext,
    type LayerTableEntry, type TextStyleTableEntry, type DimStyleTableEntry,
    DEFAULT_DXF_VERSION, resolveDxfVersion,
} from './types';

export { DEFAULT_DXF_VERSION };
import { hexToAci } from './colors';
import { bakeDimensionBlocks } from './dimBlockBake';
import { writeHeader } from './sections/header';
import { writeTables, LINETYPE_DEFS } from './sections/tables';
import { writeBlocks, planFromBlockDef, type BlockWritePlan } from './sections/blocks';
import { writeEntitiesSection } from './sections/entities';
import { writeObjectsSection } from './sections/objects';

export interface DxfWriteOptions {
    /** Version target (AC1014…AC1032). Default AC1032 (DXF 2018). */
    version?: DxfVersion;
    /** Dependency-closed block definitions (WBLOCK). Default: all defs. */
    blocks?: BlockDef[];
    /** Dependency-closed layers (WBLOCK). Default: all app layers. */
    layers?: Layer[];
}

const MODEL_SPACE = '*Model_Space';
const PAPER_SPACE = '*Paper_Space';

export function writeDxfNative(entities?: Entity[], opts?: DxfWriteOptions): string {
    const app = useAppStore.getState();
    const list = (entities ?? Object.values(app.entities)).filter((e) => e.visible);
    const layers: Layer[] = opts?.layers ?? Object.values(app.layers);
    const defs = opts?.blocks ?? Object.values(useBlockStore.getState().defs);
    const version: DxfVersionInfo = resolveDxfVersion(opts?.version ?? DEFAULT_DXF_VERSION);

    // ---- anonymous dimension blocks (app-side *D baking, OCS parity) ----
    const dimBlocks = bakeDimensionBlocks(list);

    // ---- handle allocator + table record pools ----
    const handles = new HandleAllocator();
    const layerEntries = new Map<string, LayerTableEntry>();
    layers.forEach((layer) => {
        layerEntries.set(layer.id, {
            handle: handles.nextTableHandle(),
            layer,
            aci: hexToAci(layer.color) === 256 ? 7 : hexToAci(layer.color),
        });
    });

    const ltypeEntries = new Map<string, { handle: string; def: (typeof LINETYPE_DEFS)[number] }>();
    LINETYPE_DEFS.forEach((def) => ltypeEntries.set(def.name, { handle: handles.nextTableHandle(), def }));

    const styleEntries = new Map<string, TextStyleTableEntry>();
    const textStyles = useTextStyleStore.getState().styles;
    const standardSource = textStyles['standard'];
    styleEntries.set('STANDARD', {
        handle: handles.nextTableHandle(),
        name: 'STANDARD',
        height: standardSource?.fontSize ?? 0.3,
        fontFile: fontFileOf(standardSource?.fontFamily ?? 'Arial'),
        bigFont: '',
        alignment: standardSource?.alignment ?? 'left',
    });
    Object.values(textStyles).forEach((s) => {
        if (s.id === 'standard') return;
        styleEntries.set(s.id, {
            handle: handles.nextTableHandle(),
            name: s.name || s.id,
            height: s.fontSize,
            fontFile: fontFileOf(s.fontFamily),
            bigFont: '',
            alignment: s.alignment,
        });
    });

    const dimStyleEntries = new Map<string, DimStyleTableEntry>();
    Object.values(useDimStyleStore.getState().styles).forEach((s) => {
        const lineAci = hexToAci(s.lineColor);
        const textAci = hexToAci(s.textColor);
        dimStyleEntries.set(s.id, {
            handle: handles.nextTableHandle(),
            name: s.name || s.id,
            scale: s.scale, arrowSize: s.arrowSize,
            extensionOffset: s.extensionOffset, extensionOvershoot: s.extensionOvershoot,
            textHeight: s.textHeight, textGap: s.textGap,
            precision: s.precision, suffix: s.suffix,
            lineColorAci: lineAci === 256 ? 0 : lineAci,
            textColorAci: textAci === 256 ? 0 : textAci,
        });
    });

    const blockRecords = new Map<string, string>();
    blockRecords.set(MODEL_SPACE, handles.nextTableHandle());
    blockRecords.set(PAPER_SPACE, handles.nextTableHandle());
    defs.forEach((def) => blockRecords.set(def.name, handles.nextTableHandle()));
    dimBlocks.forEach((b) => blockRecords.set(b.name, handles.nextTableHandle()));

    const ctx: DxfWriteContext = {
        version,
        handles,
        layerEntries,
        ltypeEntries,
        styleEntries,
        dimStyleEntries,
        blockRecords,
        modelSpaceRecord: blockRecords.get(MODEL_SPACE)!,
        paperSpaceRecord: blockRecords.get(PAPER_SPACE)!,
        plotStyleNormal: handles.nextTableHandle(),
        dimBlocks,
        // Reserved up front (same pool, deterministic order) so the HEADER's
        // $HANDSEED — written before any section — still covers every handle.
        tableShells: {
            LTYPE: handles.nextTableHandle(),
            LAYER: handles.nextTableHandle(),
            STYLE: handles.nextTableHandle(),
            APPID: handles.nextTableHandle(),
            DIMSTYLE: handles.nextTableHandle(),
            BLOCK_RECORD: handles.nextTableHandle(),
        },
        appIdHandles: [handles.nextTableHandle(), handles.nextTableHandle()],
    };

    // ---- block write plans (spaces + defs + baked dimension blocks) ----
    const plans: BlockWritePlan[] = [
        { name: MODEL_SPACE, recordHandle: ctx.modelSpaceRecord, base: [0, 0, 0], children: [] },
        { name: PAPER_SPACE, recordHandle: ctx.paperSpaceRecord, base: [0, 0, 0], children: [] },
        ...defs.map((def) => planFromBlockDef(def, blockRecords.get(def.name)!)),
        ...[...dimBlocks.values()].map((b) => ({
            name: b.name,
            recordHandle: blockRecords.get(b.name)!,
            base: [0, 0, 0] as [number, number, number],
            anonymous: true,
            children: b.entities,
        })),
    ];

    // ---- pre-allocate every entity handle so $HANDSEED is truthful ----
    preallocateHandles(ctx, list, plans);

    // ---- emit ----
    const w = new DxfStreamWriter();
    writeHeader(w, ctx, list);
    writeClasses(w);
    writeTables(w, ctx);
    writeBlocks(w, ctx, plans);
    writeEntitiesSection(w, ctx, list);
    writeObjectsSection(w, ctx);
    w.pair(0, 'EOF');
    return w.toString();
}

/** CLASSES: proxies the minimal object graph relies on. */
function writeClasses(w: DxfStreamWriter): void {
    w.pair(0, 'SECTION').pair(2, 'CLASSES');
    w.pair(0, 'CLASS').pair(1, 'ACDBDICTIONARYWDFLT').pair(2, 'AcDbDictionaryWithDefault')
        .pair(90, 0).pair(280, 0).pair(281, 1);
    w.pair(0, 'CLASS').pair(1, 'ACAD_TABLE').pair(2, 'AcDbTable')
        .pair(90, 0).pair(280, 0).pair(281, 1);
    w.pair(0, 'ENDSEC');
}

function fontFileOf(fontFamily: string): string {
    const primary = String(fontFamily ?? '').split(',')[0]?.trim() || 'Arial';
    return `${primary.replace(/['"]/g, '').replace(/\s+/g, '').toLowerCase() || 'arial'}.ttf`;
}

/**
 * Touch every handle the write pass will allocate, in the same order the
 * writers do, so $HANDSEED covers the whole file. Ordering parity with the
 * emitters is what keeps the session map deterministic.
 */
function preallocateHandles(ctx: DxfWriteContext, list: Entity[], plans: BlockWritePlan[]): void {
    list.forEach((e) => {
        ctx.handles.entityHandle(e.id);
        ctx.handles.entityHandle(`${e.id}#fb`); // fallback outline slot
    });
    plans.forEach((plan) => {
        ctx.handles.entityHandle(`dxf:blockhdr:${plan.name}`);
        ctx.handles.entityHandle(`dxf:blockend:${plan.name}`);
        plan.children.forEach((c) => ctx.handles.entityHandle(c.id));
    });
    const defs = useBlockStore.getState().defs;
    [...list, ...plans.flatMap((p) => p.children)].forEach((e) => {
        const d = e.data as Record<string, any>;
        if (e.type === 'insert' || e.type === 'block') {
            const def = defs[String(d.blockName ?? '')];
            const values = (d.attributeValues as Record<string, string>) ?? {};
            if (def && def.attributes.length > 0 && Object.keys(values).length > 0) {
                def.attributes.forEach((_a, i) => ctx.handles.entityHandle(`dxf:attr:${e.id}:${i}`));
                ctx.handles.entityHandle(`dxf:seqend:${e.id}`);
            }
        }
        if (e.type === 'leader' && String(d.text ?? '')) {
            ctx.handles.entityHandle(`dxf:${e.id}#note`);
        }
        if (e.type === 'solid' && !(d.flat || d.wipeout || d.gradient) && Array.isArray(d.indices)) {
            d.indices.forEach((_tri: number[], i: number) => ctx.handles.entityHandle(`${e.id}#f${i}`));
        }
    });
    ctx.handles.entityHandle('dxf:rootdict');
    ctx.handles.entityHandle('dxf:plotstyledict');
}
